import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import i18n from '../i18n.js';

interface LocaleContextValue {
  locale: string;
  setLocale: (locale: string) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState(i18n.language ?? 'id');

  useEffect(() => {
    const handler = (lng: string) => setLocaleState(lng);
    i18n.on('languageChanged', handler);
    return () => { i18n.off('languageChanged', handler); };
  }, []);

  const setLocale = useCallback((lng: string) => {
    void i18n.changeLanguage(lng);
    localStorage.setItem('locale', lng);
  }, []);

  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
  return ctx;
}
